/**
 * Ready-made customer email templates.
 *
 * The composer opens on a picker of these so a mechanic never starts from a
 * blank box — pick "MOT reminder", the subject and body are written, tweak and
 * send. Each template is a pure function of the customer/business/vehicle
 * details the composer already has, so there are no unresolved {{placeholders}}
 * left in the text the user edits.
 *
 * `id` is also written to email_log.template_type, so the customer's
 * communication history shows what kind of message was sent.
 */

export interface EmailTemplateContext {
    /** Customer's first name, or 'there'. */
    firstName: string;
    /** Trading name of the workshop. */
    businessName: string;
    /** e.g. "2016 Ford Focus (AB12 CDE)" — empty when no vehicle is known. */
    vehicleLabel: string;
    /** Registration on its own, e.g. "AB12 CDE". */
    vrm: string;
    /** MOT expiry formatted for UK reading, e.g. "3 Sep 2026". Empty if unknown. */
    motDue: string;
}

export interface EmailTemplate {
    id: string;
    label: string;
    /** One-line description shown under the label in the picker. */
    hint: string;
    /** Whether the copy reads better with a vehicle selected. */
    usesVehicle: boolean;
    /**
     * Attachment the composer ticks on by default when this template is picked.
     * The user can still add or remove any of them — see AttachmentKind in
     * CustomerEmailModal. A template that names one it cannot build (no service
     * history on file, no diagnostic report) simply opens with nothing ticked.
     */
    defaultAttachment?: 'service_history' | 'diagnostic';
    subject: (c: EmailTemplateContext) => string;
    body: (c: EmailTemplateContext) => string;
}

const sign = (c: EmailTemplateContext) => `Kind regards,\n${c.businessName}`;

export const EMAIL_TEMPLATES: EmailTemplate[] = [
    {
        id: 'custom',
        label: 'Blank message',
        hint: 'Start from an empty email',
        usesVehicle: false,
        subject: () => '',
        body: (c) => `Hi ${c.firstName},\n\n\n\n${sign(c)}`,
    },
    {
        id: 'service_history',
        label: 'Service history',
        hint: 'Attach their full service history as a PDF',
        usesVehicle: true,
        defaultAttachment: 'service_history',
        subject: (c) => `Service history${c.vrm ? ` — ${c.vrm}` : ''} from ${c.businessName}`,
        body: (c) =>
            `Hi ${c.firstName},\n\n` +
            `Please find attached the service history for ${c.vehicleLabel || 'your vehicle'}, covering the work we've carried out.\n\n` +
            `Keep this for your records — it's handy proof of a maintained vehicle.\n\n` +
            sign(c),
    },
    {
        id: 'service_due',
        label: 'Service due',
        hint: 'Nudge them that a service is due',
        usesVehicle: true,
        subject: (c) => `Service due${c.vrm ? ` — ${c.vrm}` : ''}`,
        body: (c) =>
            `Hi ${c.firstName},\n\n` +
            `Our records show ${c.vehicleLabel || 'your vehicle'} is about due for its next service.\n\n` +
            `Keeping to the service schedule protects the warranty, keeps running costs down and helps the car hold its value. ` +
            `Reply to this email or give us a call and we'll find a slot that suits you.\n\n` +
            sign(c),
    },
    {
        id: 'mot_reminder',
        label: 'MOT reminder',
        hint: 'MOT expiry is coming up',
        usesVehicle: true,
        subject: (c) => `Your MOT is due soon${c.vrm ? ` — ${c.vrm}` : ''}`,
        body: (c) =>
            `Hi ${c.firstName},\n\n` +
            `This is a friendly reminder that the MOT on ${c.vehicleLabel || 'your vehicle'} is due` +
            `${c.motDue ? ` on ${c.motDue}` : ' soon'}.\n\n` +
            `Driving without a valid MOT can invalidate your insurance, so it's best to get it booked in early. ` +
            `Just reply or give us a call and we'll sort a convenient time.\n\n` +
            sign(c),
    },
    {
        id: 'payment_reminder',
        label: 'Payment reminder',
        hint: 'Chase an unpaid invoice, politely',
        usesVehicle: false,
        subject: () => 'Your invoice is still outstanding',
        body: (c) =>
            `Hi ${c.firstName},\n\n` +
            `Just a gentle reminder that our invoice for the recent work is still showing as unpaid on our system.\n\n` +
            `If you've already sent payment, thank you — please ignore this message. If not, you can pay using the link on the invoice, ` +
            `or reply to this email and we'll sort out an alternative.\n\n` +
            sign(c),
    },
    {
        id: 'vehicle_ready',
        label: 'Ready for collection',
        hint: 'Work is finished, come and get it',
        usesVehicle: true,
        subject: (c) => `${c.vrm || 'Your vehicle'} is ready for collection`,
        body: (c) =>
            `Hi ${c.firstName},\n\n` +
            `Good news — the work on ${c.vehicleLabel || 'your vehicle'} is finished and it's ready to collect.\n\n` +
            `Pop in any time during opening hours. If you'd like us to run through what was done, just say and we'll talk you through it.\n\n` +
            sign(c),
    },
    {
        id: 'diagnostic_followup',
        label: 'Health check results',
        hint: 'Attach the diagnostic report and offer to book the work',
        usesVehicle: true,
        defaultAttachment: 'diagnostic',
        subject: (c) => `Your health check results${c.vrm ? ` — ${c.vrm}` : ''}`,
        body: (c) =>
            `Hi ${c.firstName},\n\n` +
            `Thanks for bringing ${c.vehicleLabel || 'your vehicle'} in. We've been through it and attached the write-up — ` +
            `what we found, how urgent each thing is, and roughly what it would cost to put right.\n\n` +
            `The costs on it are an estimate, not a bill, and nothing is booked in. Nothing has to be done today either — ` +
            `happy to talk through what's urgent and what can wait. Reply here and we'll find you a slot.\n\n` +
            sign(c),
    },
    {
        id: 'thank_you',
        label: 'Thank you / feedback',
        hint: 'After the job, ask how it went',
        usesVehicle: false,
        subject: (c) => `Thanks from ${c.businessName}`,
        body: (c) =>
            `Hi ${c.firstName},\n\n` +
            `Thanks for choosing us — we appreciate the business.\n\n` +
            `If everything's running as it should, we'd be grateful for a quick review. And if anything isn't quite right, ` +
            `tell us first and we'll put it straight.\n\n` +
            sign(c),
    },
];

export const templateById = (id: string): EmailTemplate =>
    EMAIL_TEMPLATES.find((t) => t.id === id) ?? EMAIL_TEMPLATES[0];
